/* ============================================================
   BOOKSCART — WebGL 3D Book (Three.js, lazy-loaded)
   Renders a real 3D book mesh with the cover image as texture.
   Falls back to a flat image if WebGL/Three fails.
   ============================================================ */
(function () {
  'use strict';

  var THREE_SRC = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js';
  var loaded = false, loading = false, queue = [];

  function loadThree(cb) {
    if (loaded) { cb(); return; }
    queue.push(cb);
    if (loading) return;
    loading = true;
    var s = document.createElement('script');
    s.src = THREE_SRC;
    s.onload = function () { loaded = true; queue.forEach(function (f) { f(); }); queue = []; };
    s.onerror = function () { queue.forEach(function (f) { f(new Error('three-failed')); }); queue = []; };
    document.head.appendChild(s);
  }

  function webglSupported() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }

  function initBook(el) {
    var coverUrl = el.getAttribute('data-cover');
    var fallback = el.querySelector('[data-book-fallback]');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function showFallback() { if (fallback) fallback.style.display = ''; el.classList.add('is-fallback'); }

    if (!webglSupported() || !coverUrl) { showFallback(); return; }

    loadThree(function (err) {
      if (err || !window.THREE) { showFallback(); return; }
      try { build(el, coverUrl, fallback, reduce); }
      catch (e) { showFallback(); }
    });
  }

  function build(el, coverUrl, fallback, reduce) {
    var THREE = window.THREE;
    var W = el.clientWidth, H = el.clientHeight || Math.round(W * 1.2);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(35, W / H, 0.1, 100);
    camera.position.set(0, 0, 7.2);

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H);
    el.appendChild(renderer.domElement);
    if (fallback) fallback.style.display = 'none';

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    var key = new THREE.DirectionalLight(0xffffff, 1.1); key.position.set(3, 4, 6); scene.add(key);
    var rim = new THREE.DirectionalLight(0xffd9b3, 0.6); rim.position.set(-4, 2, -3); scene.add(rim);

    // Book dimensions (w,h,depth)
    var bw = 2.6, bh = 3.7, bd = 0.55;

    // Materials
    var loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    var pageMat = new THREE.MeshStandardMaterial({ color: 0xf3ecdd, roughness: 0.95 });
    var spineMat = new THREE.MeshStandardMaterial({ color: 0x1a1a18, roughness: 0.6 });
    var backMat = new THREE.MeshStandardMaterial({ color: 0x24211c, roughness: 0.7 });
    var coverMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.55 });

    // Order of BoxGeometry materials: +x,-x,+y,-y,+z,-z
    var materials = [
      pageMat,   // right (page edges)
      spineMat,  // left (spine)
      pageMat,   // top
      pageMat,   // bottom
      coverMat,  // front (cover) <- texture
      backMat    // back
    ];
    var geo = new THREE.BoxGeometry(bw, bh, bd);
    var mesh = new THREE.Mesh(geo, materials);
    // slight bevel feel via edge darkening skipped for perf
    var group = new THREE.Group();
    group.add(mesh);
    group.rotation.set(0.12, -0.5, 0);
    scene.add(group);

    // Load cover texture
    loader.load(coverUrl, function (tex) {
      tex.colorSpace = THREE.SRGBColorSpace || tex.colorSpace;
      coverMat.map = tex; coverMat.color.set(0xffffff); coverMat.needsUpdate = true;
    });

    // Interaction: drag to spin + idle auto-rotate + inertia
    var targetY = -0.5, targetX = 0.12, curY = -0.5, curX = 0.12, velY = 0;
    var dragging = false, lastX = 0, lastY = 0, idle = true;

    function onDown(x, y) { dragging = true; idle = false; lastX = x; lastY = y; }
    function onMove(x, y) {
      if (!dragging) return;
      var dx = (x - lastX) * 0.01, dy = (y - lastY) * 0.008;
      targetY += dx; targetX = Math.max(-0.6, Math.min(0.6, targetX + dy)); velY = dx;
      lastX = x; lastY = y;
    }
    function onUp() { dragging = false; }

    var dom = renderer.domElement;
    dom.style.cursor = 'grab';
    dom.addEventListener('mousedown', function (e) { dom.style.cursor = 'grabbing'; onDown(e.clientX, e.clientY); });
    window.addEventListener('mousemove', function (e) { onMove(e.clientX, e.clientY); });
    window.addEventListener('mouseup', function () { dom.style.cursor = 'grab'; onUp(); });
    dom.addEventListener('touchstart', function (e) { onDown(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
    dom.addEventListener('touchmove', function (e) { onMove(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
    dom.addEventListener('touchend', onUp);

    // Mouse parallax when not dragging (desktop)
    var fine = window.matchMedia('(hover:hover) and (pointer:fine)').matches;
    if (fine && !reduce) {
      el.addEventListener('mousemove', function (e) {
        if (dragging) return;
        var r = el.getBoundingClientRect();
        targetY = -0.5 + ((e.clientX - r.left) / r.width - 0.5) * 0.8;
        targetX = 0.12 - ((e.clientY - r.top) / r.height - 0.5) * 0.5;
      });
    }

    function animate() {
      requestAnimationFrame(animate);
      if (idle && !reduce) { targetY += 0.003; }
      if (!dragging && Math.abs(velY) > 0.0002) { targetY += velY; velY *= 0.95; }
      curY += (targetY - curY) * 0.1;
      curX += (targetX - curX) * 0.1;
      group.rotation.y = curY;
      group.rotation.x = curX;
      renderer.render(scene, camera);
    }
    animate();

    // Resize
    window.addEventListener('resize', function () {
      var w = el.clientWidth, h = el.clientHeight || Math.round(w * 1.2);
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    });
  }

  // Lazy init when in view
  function boot() {
    var els = document.querySelectorAll('[data-webgl-book]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(initBook); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { initBook(en.target); io.unobserve(en.target); }
      });
    }, { rootMargin: '200px' });
    els.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
